import React from 'react';
import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  Typography,
  Box,
  type SxProps,
  type Theme,
} from '@mui/material';

import { colors } from '../../theme/tokens';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: { value: number; label: string };
  accentColor?: string;
  sx?: SxProps<Theme>;
}

export function StatCard({ title, value, subtitle, icon, trend, accentColor, sx }: StatCardProps) {
  const trendPositive = trend && trend.value >= 0;

  return (
    <MuiCard sx={{ height: '100%', ...sx }}>
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            {title}
          </Typography>
          {icon && (
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor: accentColor ? `${accentColor}22` : 'rgba(76,175,80,0.12)',
                color: accentColor ?? colors.primary[400],
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>
          {value}
        </Typography>

        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}

        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: trendPositive ? colors.success.light : colors.error.light, fontWeight: 600 }}
            >
              {trendPositive ? '+' : ''}{trend.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {trend.label}
            </Typography>
          </Box>
        )}
      </CardContent>
    </MuiCard>
  );
}

interface SectionCardProps {
  title?: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  noPadding?: boolean;
}

export function SectionCard({ title, subtitle, action, children, sx, contentSx, noPadding }: SectionCardProps) {
  return (
    <MuiCard sx={sx}>
      {(title || action) && (
        <CardHeader
          title={title && <Typography variant="h6">{title}</Typography>}
          subheader={subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
          action={action}
          sx={{
            pb: 0,
            px: 3,
            pt: 2.5,
            gap: 1.5,
            alignItems: 'center',
            flexWrap: 'wrap',
            '& .MuiCardHeader-content': { flex: '1 1 220px', minWidth: 0 },
            '& .MuiCardHeader-action': { flex: '0 1 auto', m: 0, maxWidth: '100%', minWidth: 0 },
          }}
        />
      )}
      <CardContent sx={{ p: noPadding ? 0 : 3, '&:last-child': { pb: noPadding ? 0 : 3 }, ...contentSx }}>
        {children}
      </CardContent>
    </MuiCard>
  );
}
